import { useCallback, useEffect, useRef, useState } from "react";

const useStringStateWithDebounce = (
  initialValue: string,
  debounceTime: number,
) => {
  const [value, setValue] = useState<string>(initialValue);

  // Use useRef to keep track of the debounce timeout without triggering re-renders
  const debounceTimeoutRef = useRef<number | null>(null);

  const flush = useCallback(() => {
    setValue(""); // Reset the value after the debounce time has passed
  }, []);

  const append = useCallback((char: string) => {
    setValue((prevValue) => prevValue + char);
  }, []);

  const backspace = useCallback(() => {
    setValue((prevValue) => prevValue.slice(0, -1));
  }, []);

  useEffect(() => {
    // If there's an existing timer, clear it
    if (debounceTimeoutRef.current !== null) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Reset the debounce timer
    debounceTimeoutRef.current = window.setTimeout(flush, debounceTime);

    // Cleanup function to clear the timeout
    return () => {
      if (debounceTimeoutRef.current !== null) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [value, debounceTime, flush]);

  return { value, append, backspace, flush };
};

export default useStringStateWithDebounce;
